import { z } from 'zod';
import { isoTimestampSchema, uuidSchema } from './common';

/* Budgets (P1 §5.6, ADR-0023). Amounts: integer minor units, branch currency. */

const periodSchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'YYYY-MM expected');
const amountSchema = z.number().int().min(0).refine(Number.isSafeInteger);
const codeText = z.string().trim().min(1).max(60);

export const budgetPositionSchema = z.object({
  approved_minor: z.number().int(),
  committed_minor: z.number().int(),
  actual_minor: z.number().int(),
  available_minor: z.number().int(),
  utilization_bps: z.number().int().nullable(),
  over_budget: z.boolean(),
});
export type BudgetPosition = z.infer<typeof budgetPositionSchema>;

export const budgetSchema = z.object({
  id: uuidSchema,
  period: periodSchema,
  cost_center: z.string(),
  category: z.string(),
  currency: z.string(),
  status: z.enum(['draft', 'approved', 'revision_pending', 'closed']),
  approved_amount_minor: z.number().int(),
  proposed_amount_minor: z.number().int().nullable(),
  over_budget_policy: z.enum(['block', 'escalate']),
  revision: z.number().int(),
  proposed_by: uuidSchema.nullable(),
  proposed_by_name: z.string().nullable(),
  approved_by_name: z.string().nullable(),
  approved_at: isoTimestampSchema.nullable(),
  notes: z.string().nullable(),
  version: z.number().int(),
  position: budgetPositionSchema,
});
export type Budget = z.infer<typeof budgetSchema>;

export const budgetListQuerySchema = z.object({ period: periodSchema.optional() });
export const budgetListResponseSchema = z.object({
  currency: z.string(),
  period: periodSchema.nullable(),
  definitions: z.record(z.string(), z.string()),
  items: z.array(budgetSchema),
  totals: budgetPositionSchema,
});

export const createBudgetRequestSchema = z.object({
  period: periodSchema,
  cost_center: codeText,
  category: codeText,
  amount_minor: amountSchema,
  over_budget_policy: z.enum(['block', 'escalate']).default('block'),
  notes: z.string().trim().min(1).max(300).nullable().default(null),
});
/** Proposing a new amount on an approved budget opens a revision that needs approval. */
export const updateBudgetRequestSchema = z.object({
  expected_version: z.number().int().nonnegative(),
  amount_minor: amountSchema.optional(),
  over_budget_policy: z.enum(['block', 'escalate']).optional(),
  notes: z.string().trim().min(1).max(300).nullable().optional(),
});
export const budgetDecisionRequestSchema = z.object({
  expected_version: z.number().int().nonnegative(),
  note: z.string().trim().min(1).max(300).optional(),
});

export const budgetEntrySchema = z.object({
  id: uuidSchema,
  kind: z.enum(['commitment', 'actual']),
  source_type: z.string(),
  source_id: uuidSchema,
  source_label: z.string().nullable(),
  amount_minor: z.number().int(),
  reason: z.string().nullable(),
  created_by_name: z.string().nullable(),
  created_at: isoTimestampSchema,
});
export const budgetEntriesResponseSchema = z.object({
  budget: budgetSchema,
  entries: z.array(budgetEntrySchema),
});
